// Materiality upload slots: align uploadedFiles[], uploadedFileEntries[], documentAnalysisResults[] by index. Placeholders use PERSISTED_UPLOAD_SLOT_MARKER.
// Analyze: upload new File slots only; reuse file_id rows; skip analyze-documents API when cached documentAnalysisResults exist; prune rows without file_id.

export const MATERIALITY_ASSESSMENT_STORAGE_KEY = 'materialityAssessmentState';

// Distinguishes persisted placeholder objects from real browser File instances (optional marker).
export const PERSISTED_UPLOAD_SLOT_MARKER = '__persistedOnly';

// True if the slot is an actual File (uploadable); false for JSON-restored placeholders.
// Guard: `instanceof File` throws if `File` is undefined (SSR, some test runners).
export function isRealUploadFile(file) {
  return typeof File !== 'undefined' && file instanceof File;
}

// Descriptor shape per slot in localStorage: { name, size, type } (UploadFileDescriptor).

// Build serializable descriptors from live File[] or placeholder slots (never persist raw File).
export function descriptorsFromUploadSlots(uploadSlots) {
  return (uploadSlots || []).map((f) =>
    isRealUploadFile(f)
      ? { name: f.name, size: f.size, type: f.type }
      : { name: f?.name ?? '', size: f?.size ?? 0, type: f?.type ?? '' }
  );
}

// Rebuild placeholder slots for UI after refresh (no bytes; user must re-select files to upload again).
export function persistedUploadSlotsFromDescriptors(descriptors) {
  return (descriptors || []).map((d) => ({
    name: d.name || 'Document',
    size: d.size ?? 0,
    type: d.type ?? '',
    [PERSISTED_UPLOAD_SLOT_MARKER]: true,
  }));
}

// Display name for a slot (File, placeholder, or fallback from analysis filename).
export function getUploadSlotLabel(slot, index, fallbackName) {
  if (isRealUploadFile(slot)) return slot.name;
  if (slot?.name) return slot.name;
  return fallbackName || `Document ${index + 1}`;
}

// Parse saved draft JSON into descriptor rows (supports uploadedFileDescriptors or legacy uploadedFiles + analysis names).
export function getStoredUploadDescriptors(saved) {
  if (!saved || typeof saved !== 'object') return [];

  const uploadedFileDescriptors = saved.uploadedFileDescriptors;
  if (Array.isArray(uploadedFileDescriptors) && uploadedFileDescriptors.length > 0) {
    return uploadedFileDescriptors.map((d) => ({
      name: d?.name ?? '',
      size: d?.size ?? 0,
      type: d?.type ?? '',
    }));
  }

  const legacyFiles = saved.uploadedFiles;
  const analysis = saved.documentAnalysisResults;
  if (!Array.isArray(legacyFiles) || legacyFiles.length === 0) return [];

  return legacyFiles.map((f, i) => ({
    name: f?.name || analysis?.[i]?.filename || '',
    size: f?.size ?? 0,
    type: f?.type ?? '',
  }));
}

// Save draft when there are upload slots, past step 1, or lingering server entries.
export function shouldPersistMaterialityDraft(p) {
  return (
    p.uploadSlotCount > 0 ||
    p.currentStep > 1 ||
    p.uploadedFileEntriesCount > 0
  );
}

// Remove storage when nothing to resume and user is still on step 1.
export function shouldClearMaterialityDraftStorage(p) {
  return !shouldPersistMaterialityDraft(p) && p.currentStep === 1;
}

// True when this slot already has a server-backed id (no init/GCS upload needed for Analyze Documents).
export function entryHasServerFileId(entry) {
  return Boolean(entry && (entry.file_id || entry.uuid));
}

// Merge by slot index using in-memory entries only (materiality draft is not persisted).
export function resolveUploadedFileEntriesForAnalyze(uploadSlots, entriesFromState) {
  const fromState = Array.isArray(entriesFromState) ? entriesFromState : [];
  return (uploadSlots || []).map((_, i) => fromState[i] ?? null);
}

// Per-slot plan: reuse if file_id/uuid exists (even when slot is still a File); upload only when no server id; else missing.
export function getAnalyzeDocumentsSlotActions(uploadSlots, uploadedFileEntries) {
  const slots = uploadSlots || [];
  const entries = Array.isArray(uploadedFileEntries) ? uploadedFileEntries : [];
  const actions = slots.map((slot, i) => {
    const entry = entries[i];
    if (entryHasServerFileId(entry)) return { index: i, type: 'reuse', entry };
    if (isRealUploadFile(slot)) return { index: i, type: 'upload', file: slot };
    return { index: i, type: 'missing' };
  });
  return {
    actions,
    isValid: actions.every((a) => a.type !== 'missing'),
  };
}

// Normalize a persisted server entry for in-memory state (strip file_ref, ensure file_id).
export function mergedEntryFromReuseSlot(entry) {
  if (!entry) return null;
  const { file_ref, ...rest } = entry;
  return {
    ...rest,
    file_id: rest.file_id || rest.uuid,
  };
}

// Trim or pad a parallel array to match upload slot count, then append null placeholders for new files.
export function alignAndExtendParallelArray(arr, slotCountBeforeAppend, appendedSlotCount) {
  const raw = Array.isArray(arr) ? [...arr] : [];
  const truncated =
    raw.length > slotCountBeforeAppend ? raw.slice(0, slotCountBeforeAppend) : raw;
  const padLen = Math.max(0, slotCountBeforeAppend - truncated.length);
  const padded = [...truncated, ...Array(padLen).fill(null)];
  const add = Math.max(0, appendedSlotCount);
  return add === 0 ? padded : [...padded, ...Array(add).fill(null)];
}

// Build fast lookup for slot actions (avoid O(n^2) find-per-index in Analyze handler).
export function analyzeSlotActionsByIndex(actions) {
  return Object.fromEntries((actions || []).map((a) => [a.index, a]));
}

// Request body rows for analyze-documents: same order as input; throws if any row lacks a server id.
export function entriesForAnalyzeDocumentsRequest(entries) {
  const list = entries || [];
  return list.map((e, i) => {
    if (!entryHasServerFileId(e)) {
      throw new Error(`Analyze documents: missing file_id at payload index ${i}`);
    }
    const { file_ref, ...rest } = e;
    return {
      ...rest,
      file_id: rest.file_id || rest.uuid,
    };
  });
}

// True if we already have a prior analyze-documents row for this slot (skip re-calling API on reuse).
export function hasDocumentAnalysisCache(doc) {
  return Boolean(doc && (doc.filename || doc.category));
}

// After removing upload slot at removedIndex, remap checkbox indices to new positions.
export function remapIndicesAfterRemoval(indices, removedIndex) {
  if (!Array.isArray(indices)) return [];
  return [
    ...new Set(
      indices
        .filter((i) => i !== removedIndex)
        .map((i) => (i > removedIndex ? i - 1 : i))
    ),
  ].sort((a, b) => a - b);
}

// Drop upload rows that are only placeholders with no server file_id (stale UI after refresh). Keeps real File[] slots.
export function pruneMaterialityParallelArrays({
  uploadSlots,
  uploadedFileEntries,
  documentAnalysisResults,
  selectedDocumentIndices,
  riskAssessmentDocIndices,
}) {
  const slots = uploadSlots || [];
  const entries = Array.isArray(uploadedFileEntries) ? uploadedFileEntries : [];
  const docs = Array.isArray(documentAnalysisResults) ? documentAnalysisResults : [];
  const n = slots.length;

  if (n === 0) {
    const hadOrphans = entries.length > 0 || docs.length > 0;
    return {
      changed: hadOrphans,
      uploadSlots: [],
      uploadedFileEntries: [],
      documentAnalysisResults: [],
      selectedDocumentIndices: [],
      riskAssessmentDocIndices: [],
    };
  }

  const keep = slots
    .map((slot, i) => ({ slot, i, keep: isRealUploadFile(slot) || entryHasServerFileId(entries[i]) }))
    .filter(({ keep: k }) => k)
    .map(({ i }) => i);

  const remap = (indices) => {
    if (!Array.isArray(indices) || indices.length === 0) return [];
    const mapOldToNew = Object.fromEntries(keep.map((old, ni) => [old, ni]));
    return [...new Set(indices.map((old) => mapOldToNew[old]).filter((v) => v !== undefined))].sort(
      (a, b) => a - b
    );
  };

  const nextSel = remap(selectedDocumentIndices);
  const nextRisk = remap(riskAssessmentDocIndices);
  const sortNum = (a, b) => a - b;
  const sortedEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    const sa = [...a].sort(sortNum);
    const sb = [...b].sort(sortNum);
    return sa.every((v, i) => v === sb[i]);
  };
  const selectionDirty =
    !sortedEqual(nextSel, selectedDocumentIndices || []) ||
    !sortedEqual(nextRisk, riskAssessmentDocIndices || []);

  const arraysTruncated = keep.length !== n;
  const changed = arraysTruncated || selectionDirty;

  return {
    changed,
    uploadSlots: arraysTruncated ? keep.map((i) => slots[i]) : slots,
    uploadedFileEntries: arraysTruncated ? keep.map((i) => entries[i] ?? null) : entries,
    documentAnalysisResults: arraysTruncated ? keep.map((i) => docs[i] ?? null) : docs,
    selectedDocumentIndices: nextSel,
    riskAssessmentDocIndices: nextRisk,
  };
}
